import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth } from "./AuthContext";
import { EMPTY_OBJECT } from "constants/defaults";
import { ANALYTICS } from "constants/analytics";
import { useInterval } from "react-use";
import { usePage } from "hooks/usePage";
import { useAnalyticsAccessors } from "./database/useAnalyticsAccessors";
import { getDataFromDocument } from "./database/useMainAccessors";
import { fpUpdate } from "lib/fpLodash";
import { uuid } from "lib/uid";
import LZString from "lz-string";
import { safeParseInt } from "lib/numberUtils";

export const AnalyticsContext = createContext();

const getUTCKey = () => {
  const newDate = new Date();

  const utcHour = newDate.getUTCHours();
  const utcDate = newDate.getUTCDate();
  const utcFullYear = newDate.getUTCFullYear();
  const utcMonth = newDate.getUTCMonth();
  const utcKey = `${utcFullYear}-${utcMonth + 1}-${utcDate}`;

  return { utcKey, utcHour };
};

export const AnalyticsProvider = (props) => {
  const { user } = useAuth();
  const userId = user?.uid;
  const { analyticsConsent = EMPTY_OBJECT } = user || EMPTY_OBJECT;
  const {
    [ANALYTICS.PAGEVIEWS]: pageviewConsent,
    [ANALYTICS.CRASH_REPORT]: crashConsent,
  } = analyticsConsent;
  const { addIgnoreWithId, update } = useAnalyticsAccessors();
  const [page] = usePage();
  const pageCache = useRef({});
  const [hour, setHour] = useState(new Date().getUTCHours());

  const logPageview = useCallback(
    async (page) => {
      try {
        if (pageviewConsent) {
          const { utcKey, utcHour: rawHour } = getUTCKey();
          const utcHour = `pv-${rawHour}`;

          const res = await addIgnoreWithId(
            utcKey,
            {
              [utcHour]: {
                [page]: 1,
              },
            },
            { disableReadExisting: true }
          );

          if (!res) {
            await update(utcKey, (doc) => {
              const data = getDataFromDocument(doc);

              return {
                [utcHour]: fpUpdate(
                  `${page}`,
                  (prev) => safeParseInt(prev, 0) + 1,
                  data?.[utcHour] || {}
                ),
              };
            });
          }

          return true;
        }
      } catch (err) {
        console.error(err);
      }
    },
    [addIgnoreWithId, pageviewConsent, update]
  );

  const logCrash = useCallback(
    async (error) => {
      try {
        if (!user || crashConsent) {
          // send crash report for error
          const { utcKey, utcHour } = getUTCKey();

          const report = {
            error: error.message,
            stack: LZString.compressToUTF16(error?.stack || ""),
            url: window.location.host + window.location.pathname,
            hour: utcHour,
            userAgent: navigator.userAgent,
          };

          const crashData = {
            [`crash||${uuid()}`]: report,
          };
          const res = await addIgnoreWithId(utcKey, crashData, {
            disableReadExisting: true,
          });

          if (!res) {
            await update(utcKey, (doc) => {
              return crashData;
            });
          }

          return true;
        }
      } catch (err) {
        console.error(err);
      }
    },
    [addIgnoreWithId, crashConsent, update, user]
  );

  useInterval(
    () => {
      setHour(new Date().getUTCHours());
    },
    // 5 minutes
    1000 * 60 * 5
  );

  useEffect(() => {
    pageCache.current = {};
  }, [hour]);

  useEffect(() => {
    if (userId && pageviewConsent && page && !pageCache.current[page]) {
      pageCache.current[page] = true;
      logPageview(page);
      // .error(() => (pageCache.current[page] = false));
    }
  }, [hour, logPageview, page, pageviewConsent, userId]);

  const value = useMemo(
    () => ({
      analyticsConsent,
      logCrash,
    }),
    [analyticsConsent, logCrash]
  );

  return <AnalyticsContext.Provider {...props} value={value} />;
};

export const useAnalytics = () => useContext(AnalyticsContext);
